import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { motion } from "framer-motion";
import { Volume2, VolumeX } from "lucide-react";

const VIDEO_ID = "EB9e7EhLQqo";

export default function AmbientMusic() {
    const playerRef = useRef(null);
    const readyRef = useRef(false);
    const pendingRef = useRef(false);
    const pendingUnmuteRef = useRef(false);
    const [playing, setPlaying] = useState(false);

    useEffect(() => {
        const startPlayback = (p) => {
            if (pendingRef.current) {
                pendingRef.current = false;
                pendingUnmuteRef.current = true;
                p.mute();
                p.playVideo();
            }
        };
        const handleReady = (e) => {
            const p = e.target;
            p.setVolume(35);
            window.__galaPlayer = p;
            playerRef.current = p;
            readyRef.current = true;
            startPlayback(p);
        };
        const createPlayer = () => {
            if (window.__galaPlayer) {
                playerRef.current = window.__galaPlayer;
                readyRef.current = true;
                if (pendingRef.current) {
                    pendingRef.current = false;
                    window.__galaPlayer.playVideo();
                }
                return;
            }
            new window.YT.Player("yt-music-player", {
                videoId: VIDEO_ID,
                playerVars: { autoplay: 0, loop: 1, playlist: VIDEO_ID, controls: 0, disablekb: 1, playsinline: 1 },
                events: {
                    onReady: handleReady,
                    onStateChange: (e) => {
                        setPlaying(e.data === window.YT.PlayerState.PLAYING);
                        if (e.data === window.YT.PlayerState.PLAYING && pendingUnmuteRef.current) {
                            pendingUnmuteRef.current = false;
                            e.target.unMute();
                            e.target.setVolume(35);
                        }
                    },
                },
            });
        };
        if (!document.getElementById("yt-iframe-api")) {
            const tag = document.createElement("script");
            tag.id = "yt-iframe-api";
            tag.src = "https://www.youtube.com/iframe_api";
            document.head.appendChild(tag);
        }
        if (window.YT && window.YT.Player) createPlayer();
        else window.onYouTubeIframeAPIReady = createPlayer;

        const start = () => {
            if (readyRef.current && playerRef.current) playerRef.current.playVideo();
            else pendingRef.current = true;
        };
        const stop = () => {
            if (readyRef.current && playerRef.current) playerRef.current.pauseVideo();
        };
        window.addEventListener("gala:start-music", start);
        window.addEventListener("gala:stop-music", stop);
        return () => {
            window.removeEventListener("gala:start-music", start);
            window.removeEventListener("gala:stop-music", stop);
        };
    }, []);

    const toggle = () => {
        const p = playerRef.current;
        if (!p || !readyRef.current) return;
        if (playing) p.pauseVideo(); else p.playVideo();
    };

    return createPortal(
        <>
            <div aria-hidden="true" className="fixed top-0 left-0 w-px h-px opacity-0 pointer-events-none overflow-hidden">
                <div id="yt-music-player" />
            </div>
            <motion.button
                data-testid="ambient-music-toggle"
                aria-label={playing ? "Couper la musique" : "Écouter la bande-son"}
                onClick={toggle}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 2, duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
                className="fixed left-6 sm:left-12 lg:left-20 z-40 group flex items-center gap-3"
                style={{ bottom: "max(1.5rem, env(safe-area-inset-bottom))", transform: "translateZ(0)" }}
            >
                <span className={`relative flex h-11 w-11 items-center justify-center rounded-full border backdrop-blur-md transition-colors duration-500 ${
                    playing
                        ? "border-gold bg-gold text-night-deep"
                        : "border-gold/60 bg-night/40 text-gold group-hover:bg-gold group-hover:text-night-deep"
                }`}>
                    {playing && (
                        <span className="absolute inset-0 rounded-full border border-gold/60 animate-ping" style={{ animationDuration: "2.4s" }} />
                    )}
                    {playing ? <Volume2 size={16} strokeWidth={1.5} /> : <VolumeX size={16} strokeWidth={1.5} />}
                </span>
                <span className={`text-[10px] tracking-[0.3em] uppercase transition-colors duration-500 ${
                    playing ? "text-gold" : "text-white/50 group-hover:text-gold"
                }`}>
                    {playing ? "Bande-son · en cours" : "Bande-son du gala"}
                </span>
            </motion.button>
        </>,
        document.body
    );
}
